import { DATE_FORMAT, formatDateToUsaShortStandard, getEarlierDateFromDateStrings } from '@/helpers/dates';
import { formatAddress } from '@/helpers/formatAddress';
import { formatCountyResolution } from '@/helpers/formatCountyResolution';
import { formatPhoneNumber } from '@/helpers/formatPhoneNumber';
import { intercomTarget } from '@/helpers/intercomTarget';
import { DateTime } from 'luxon';

import {
  Applicant,
  ApplicantType,
  Brand,
  BrandLabels,
  DomesticContentEligibilityLabels,
  ExternalReferenceType,
  ProgramType,
  ProgramTypeLabels,
} from 'types';

import { isPalmettoFinanceUser } from '../../auth/RequirePalmettoFinanceUser';

import { useAccountDetailsContext } from './AccountDetailsContext';

interface UseAccountPropertiesParams {
  domesticContentEligibilityStatus: string;
  dcAttestmentType: string;
}

export function useAccountProperties({
  domesticContentEligibilityStatus,
  dcAttestmentType,
}: UseAccountPropertiesParams) {
  const { account, id, claims, isLightReachAdmin, isAccountEditEnabled } = useAccountDetailsContext();

  const programReferenceId = account?.externalReferenceIds?.find(
    (ref: any) => ref.type === ExternalReferenceType.doe,
  )?.id;
  const primaryAccountApplicant = account?.applicants.find(
    (applicant: Applicant) => applicant.type === ApplicantType.primary,
  );
  const secondaryAccountApplicant = account?.applicants.find(
    (applicant: Applicant) => applicant.type === ApplicantType.secondary,
  );
  const showAdditionalApplicant = secondaryAccountApplicant !== undefined;

  const formattedPrimaryApplicantCreditDOB = primaryAccountApplicant?.creditDOB
    ? formatDateToUsaShortStandard(primaryAccountApplicant?.creditDOB)
    : '';

  const accountIsHVAC = account?.programType === ProgramType.hvac;
  const accountIsWaterHeater = account?.programType === ProgramType.waterHeater;
  const accountIsGenerator = account?.programType === ProgramType.generator;
  const accountIsGeothermal = account?.programType === ProgramType.geothermal;

  return {
    heading: 'Account Details',
    editPath: isAccountEditEnabled ? `/accounts/${id}/edit` : undefined,
    values: [
      { label: 'Account Id', value: id, copyable: true },
      { label: 'External Reference', value: account?.externalReference },
      { label: 'Program Type', value: ProgramTypeLabels[account?.programType as ProgramType] },
      { label: 'Program Reference ID', value: programReferenceId },
      ...(account?.brand ? [{ label: 'Brand', value: BrandLabels[account.brand as Brand] }] : []),
      ...(isLightReachAdmin
        ? [
            {
              label: 'Date of Birth (From Credit Report)',
              value: formattedPrimaryApplicantCreditDOB,
            },
          ]
        : []),
      { label: 'Language', value: account?.language },
      {
        label: 'Primary Contact',
        value: [
          `${primaryAccountApplicant?.firstName} ${primaryAccountApplicant?.lastName}`,
          primaryAccountApplicant?.email,
          primaryAccountApplicant?.phoneNumber ? formatPhoneNumber(primaryAccountApplicant.phoneNumber) : '',
        ],
      },
      ...(showAdditionalApplicant
        ? [
            {
              label: 'Additional Contact',
              value: [
                `${secondaryAccountApplicant?.firstName} ${secondaryAccountApplicant?.lastName}`,
                secondaryAccountApplicant?.email,
                secondaryAccountApplicant?.phoneNumber
                  ? formatPhoneNumber(secondaryAccountApplicant.phoneNumber)
                  : '',
              ],
            },
          ]
        : []),
      {
        label: 'Mailing Address',
        value: formatAddress(primaryAccountApplicant?.address),
      },
      { label: 'Property Address', value: formatAddress(account?.address) },
      { label: 'County', value: formatCountyResolution(account?.countyResolution) },
      ...(isPalmettoFinanceUser(claims) && account?.isNewYorkCityAddress
        ? [{ label: 'New York City Address', value: 'Yes' }]
        : []),
      {
        label: 'Utility',
        value: account?.utility?.utilityName,
        intercomTarget: intercomTarget('account', 'utility'),
      },
      ...((accountIsHVAC || accountIsWaterHeater) && account?.propertyDetails
        ? [
            { label: 'Home Size (sq ft)', value: account.propertyDetails.homeSize },
            { label: 'Number of Existing Systems', value: account.propertyDetails.existingSystems },
            { label: 'Number of Systems Being Installed', value: account.propertyDetails.systemsBeingReplaced },
          ]
        : []),
      ...(accountIsGeothermal && account?.propertyDetails
        ? [{ label: 'Home Size (sq ft)', value: account.propertyDetails.homeSize }]
        : []),
      ...(accountIsGenerator && account?.propertyDetails
        ? [{ label: 'Number of Systems', value: account.propertyDetails.numberOfSystems }]
        : []),
      { label: 'Status', value: account?.status },
      ...(account?.itcAdderQualifications?.domesticContentEligible === true
        ? [
            {
              label: 'Domestic Content Eligibility',
              value:
                DomesticContentEligibilityLabels[
                  domesticContentEligibilityStatus as keyof typeof DomesticContentEligibilityLabels
                ] + dcAttestmentType,
            },
          ]
        : []),
      ...(account?.ptoGreenlight?.greenlightStatus
        ? [
            {
              label: 'PTO Greenlight Granted',
              value: DateTime.fromISO(
                getEarlierDateFromDateStrings(
                  account?.ptoGreenlight?.tranchedClosedOn,
                  account?.ptoGreenlight?.manualOverrideDate,
                ),
              )
                .toLocal()
                .toFormat(DATE_FORMAT),
            },
          ]
        : []),
    ],
  };
}
